// store/useAppStore.ts
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import type { APIConfig, AppSettings, AppStore, Message, VoiceState } from '../types';

const initialVoiceState: VoiceState = {
  isRecording: false,
  isConnected: false,
  isPlaying: false,
  volume: 0.8,
  isMuted: false,
  waveform: []
};

const initialSettings: AppSettings = {
  autoSpeak: true,
  continuous: false,
  language: 'en-US',
  theme: 'dark'
};

const initialAPIConfig: APIConfig = {
  openai: {
    apiKey: import.meta.env?.VITE_OPENAI_API_KEY || ''
  },
  gemini: {
    apiKey: import.meta.env?.VITE_GEMINI_API_KEY || ''
  }
};

export const useAppStore = create<AppStore>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // State
        messages: [],
        currentMessage: '',
        voiceState: initialVoiceState,
        aiProvider: 'gemini',
        isLoading: false,
        liveAPIConnected: false,
        settings: initialSettings,
        apiConfig: initialAPIConfig,

        // Actions
        addMessage: (message) => {
          const newMessage: Message = {
            ...message,
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
            // Fix: Ensure proper status assignment
            status: message.isUser
              ? 'sent'  // User messages are always 'sent'
              : (message.status || 'sent')  // AI messages use provided status or default to 'sent'
          };

          console.log('Adding message with status:', newMessage.status, newMessage);

          set((state) => ({
            messages: [...state.messages, newMessage]
          }));

          // Auto-scroll to bottom
          setTimeout(() => {
            const messagesContainer = document.getElementById('messages-container');
            if (messagesContainer) {
              messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
          }, 100);
        },

        updateMessage: (id, updates) => {
          console.log('Updating message:', id, 'with:', updates);

          set((state) => ({
            messages: state.messages.map(msg =>
              msg.id === id ? { ...msg, ...updates } : msg
            )
          }));
        },

        setCurrentMessage: (message) => set({ currentMessage: message }),

        setVoiceState: (updates) => set((state) => ({
          voiceState: { ...state.voiceState, ...updates }
        })),

        toggleProvider: () => {
          const currentState = get();
          // Disconnect Live API when switching providers
          if (currentState.liveAPIConnected) {
            set({ liveAPIConnected: false });
          }

          set((state) => ({
            aiProvider: state.aiProvider === 'openai' ? 'gemini' : 'openai'
          }));
        },

        setLoading: (loading) => {
          console.log('Setting loading state to:', loading);
          set({ isLoading: loading });
        },

        setLiveAPIConnected: (connected) => {
          console.log('Setting Live API connected to:', connected);
          set({ liveAPIConnected: connected });
        },

        updateSettings: (newSettings) => set((state) => ({
          settings: { ...state.settings, ...newSettings }
        })),

        clearMessages: () => {
          console.log('Clearing all messages');
          set({ messages: [] });
        }
      }),
      {
        name: 'voice-ai-storage',
        partialize: (state) => ({
          settings: state.settings,
          aiProvider: state.aiProvider,
          // Don't persist messages and voice state for privacy
        }),
      }
    )
  )
);

// Selectors for optimized re-renders
export const useMessages = () => useAppStore((state) => state.messages);
export const useCurrentMessage = () => useAppStore((state) => state.currentMessage);
export const useVoiceState = () => useAppStore((state) => state.voiceState);
export const useAIProvider = () => useAppStore((state) => state.aiProvider);
export const useIsLoading = () => useAppStore((state) => state.isLoading);
export const useLiveAPIConnected = () => useAppStore((state) => state.liveAPIConnected);
export const useSettings = () => useAppStore((state) => state.settings);
export const useAPIConfig = () => useAppStore((state) => state.apiConfig);

// Action selectors
export const useAppActions = () => useAppStore((state) => ({
  addMessage: state.addMessage,
  updateMessage: state.updateMessage,
  setCurrentMessage: state.setCurrentMessage,
  setVoiceState: state.setVoiceState,
  toggleProvider: state.toggleProvider,
  setLoading: state.setLoading,
  setLiveAPIConnected: state.setLiveAPIConnected,
  updateSettings: state.updateSettings,
  clearMessages: state.clearMessages
}));

// Subscribe to state changes for side effects
useAppStore.subscribe(
  (state) => state.settings.theme,
  (theme) => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }
);

useAppStore.subscribe(
  (state) => state.liveAPIConnected,
  (connected) => {
    console.log(`Live API ${connected ? 'connected' : 'disconnected'}`);
  }
);

useAppStore.subscribe(
  (state) => state.isLoading,
  (loading) => {
    console.log(`Loading state changed to: ${loading}`);
  }
);

// Auto-cleanup: Remove any messages with 'sending' status that are older than 1 minute
setInterval(() => {
  const state = useAppStore.getState();
  const now = new Date().getTime();
  const oneMinuteAgo = now - 60000;

  const hasOldSendingMessages = state.messages.some(msg =>
    msg.status === 'sending' && msg.timestamp.getTime() < oneMinuteAgo
  );

  if (hasOldSendingMessages) {
    console.log('Cleaning up old sending messages');
    useAppStore.setState({
      messages: state.messages.map(msg =>
        msg.status === 'sending' && msg.timestamp.getTime() < oneMinuteAgo
          ? { ...msg, status: 'error' }
          : msg
      ),
      isLoading: false // Also clear loading state
    });
  }
}, 30000); // Check every 30 seconds
